import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { productionJournalAPI, finishedProductsAPI, recipeCompositionsAPI } from '@/utils/api';
import { Plus, Trash2, Hash, ChevronDown, ChevronUp, CheckSquare, MessageSquare, Printer, FileDown, Pencil, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_QUALITY_CHECKS = [
  { key: 'aspect_visuel', label: 'Aspect visuel conforme', checked: false },
  { key: 'odeur', label: 'Odeur conforme', checked: false },
  { key: 'texture', label: 'Texture / consistance conforme', checked: false },
  { key: 'ph', label: 'pH vérifié', checked: false },
  { key: 'poids', label: 'Poids / volume conforme', checked: false },
  { key: 'etiquetage', label: 'Étiquetage vérifié', checked: false },
];

const ProductionJournal = () => {
  const [entries, setEntries] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [recipeIngredients, setRecipeIngredients] = useState([]);
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [editDialog, setEditDialog] = useState({ open: false, entry: null });
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    finished_product_id: '',
    quantity_lots: '',
    batch_number: '',
    ingredient_lots: [],
    quality_checks: DEFAULT_QUALITY_CHECKS.map(qc => ({ ...qc })),
    comments: ''
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const [entriesRes, prodsRes] = await Promise.all([
        productionJournalAPI.getAll(user.id || 'demo-user'),
        finishedProductsAPI.getAll(user.id || 'demo-user')
      ]);
      setEntries(entriesRes.data);
      setProducts(prodsRes.data);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleProductChange = async (fpId) => {
    setFormData({ ...formData, finished_product_id: fpId, ingredient_lots: [] });
    if (!fpId) { setRecipeIngredients([]); return; }
    try {
      const res = await recipeCompositionsAPI.getForJournal(fpId);
      const ingredients = res.data;
      setRecipeIngredients(ingredients);
      setFormData(prev => ({
        ...prev,
        finished_product_id: fpId,
        ingredient_lots: ingredients.map(ing => ({
          material_id: ing.material_id,
          material_name: ing.material_name,
          lot_number: ''
        }))
      }));
    } catch (error) {
      setRecipeIngredients([]);
    }
  };

  const updateIngredientLot = (index, lotNumber) => {
    setFormData(prev => {
      const updated = [...prev.ingredient_lots];
      updated[index] = { ...updated[index], lot_number: lotNumber };
      return { ...prev, ingredient_lots: updated };
    });
  };

  const toggleQualityCheck = (index) => {
    setFormData(prev => {
      const updated = [...prev.quality_checks];
      updated[index] = { ...updated[index], checked: !updated[index].checked };
      return { ...prev, quality_checks: updated };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await productionJournalAPI.create({
        date: formData.date,
        finished_product_id: formData.finished_product_id,
        quantity_lots: parseInt(formData.quantity_lots),
        batch_number: formData.batch_number,
        ingredient_lots: formData.ingredient_lots.filter(i => i.lot_number),
        quality_checks: formData.quality_checks.filter(qc => qc.checked).map(qc => ({ key: qc.key, label: qc.label })),
        comments: formData.comments
      }, user.id || 'demo-user');

      toast.success('Production enregistrée');
      setDialogOpen(false);
      setFormData({ date: new Date().toISOString().split('T')[0], finished_product_id: '', quantity_lots: '', batch_number: '', ingredient_lots: [], quality_checks: DEFAULT_QUALITY_CHECKS.map(qc => ({ ...qc })), comments: '' });
      setRecipeIngredients([]);
      loadData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette entrée ?')) return;
    try {
      await productionJournalAPI.delete(id);
      toast.success('Supprimée');
      loadData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handlePrintOne = async (entryId, productName) => {
    try {
      const res = await fetch(`${API_BASE}/api/production-journal/${entryId}/pdf`);
      if (!res.ok) { toast.error('Erreur génération PDF'); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Fiche_${productName.replace(/ /g, '_')}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Fiche de fabrication téléchargée');
    } catch (e) { toast.error('Erreur'); }
  };

  const handlePrintAll = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const res = await fetch(`${API_BASE}/api/production-journal/pdf/all?user_id=${user.id || 'demo-user'}`);
      if (!res.ok) { toast.error('Erreur génération PDF'); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'Journal_Production.pdf'; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Journal complet téléchargé');
    } catch (e) { toast.error('Erreur'); }
  };

  const openEditDialog = (entry) => {
    setEditData({
      date: entry.date?.split('T')[0] || entry.date,
      quantity_lots: entry.quantity_lots,
      batch_number: entry.batch_number || '',
      comments: entry.comments || '',
      quality_checks: DEFAULT_QUALITY_CHECKS.map(qc => ({
        ...qc,
        checked: (entry.quality_checks || []).some(eq => eq.key === qc.key)
      }))
    });
    setEditDialog({ open: true, entry });
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/production-journal/${editDialog.entry.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: editData.date,
          quantity_lots: parseInt(editData.quantity_lots),
          batch_number: editData.batch_number,
          comments: editData.comments,
          quality_checks: editData.quality_checks.filter(qc => qc.checked).map(qc => ({ key: qc.key, label: qc.label }))
        })
      });
      if (res.ok) {
        toast.success('Production modifiée');
        setEditDialog({ open: false, entry: null });
        loadData();
      } else { toast.error('Erreur'); }
    } catch (e) { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-8"><div className="skeleton-shimmer h-64 rounded-lg"></div></div>;

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-semibold mb-2">Journal de Production</h1>
          <p className="text-muted-foreground">Historique des fabrications avec traçabilité des lots</p>
        </div>
        <div className="flex gap-2">
          {entries.length > 0 && (
            <Button data-testid="btn-print-all" variant="outline" onClick={handlePrintAll} className="gap-2">
              <FileDown size={16} /> Exporter tout (PDF)
            </Button>
          )}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="btn-new-entry">
                <Plus size={18} className="mr-2" /> Nouvelle Production
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Enregistrer une Production</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Date</label>
                <Input data-testid="input-journal-date" type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} required />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Produit Fabriqué</label>
                <select
                  data-testid="select-journal-product"
                  className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
                  value={formData.finished_product_id}
                  onChange={(e) => handleProductChange(e.target.value)}
                  required
                >
                  <option value="">-- Sélectionner --</option>
                  {products.map(p => (
                    <option key={p.id} value={p.id}>{p.name} (Coût: {p.total_cost.toFixed(2)}€)</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Nombre de Lots</label>
                  <Input data-testid="input-journal-qty" type="number" min="1" value={formData.quantity_lots} onChange={(e) => setFormData({...formData, quantity_lots: e.target.value})} required />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">N° de Lot Production</label>
                  <Input data-testid="input-batch-number" placeholder="Ex: LOT-2026-001" value={formData.batch_number} onChange={(e) => setFormData({...formData, batch_number: e.target.value})} />
                </div>
              </div>

              {formData.ingredient_lots.length > 0 && (
                <div className="border border-border rounded-lg p-4 space-y-3 bg-muted/30">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                    <Hash size={14} />
                    Numéros de lot des ingrédients
                  </p>
                  {formData.ingredient_lots.map((ing, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-sm text-foreground min-w-[140px] truncate">{ing.material_name}</span>
                      <Input
                        data-testid={`input-ingredient-lot-${i}`}
                        placeholder="N° de lot"
                        value={ing.lot_number}
                        onChange={(e) => updateIngredientLot(i, e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Contrôles qualité */}
              <div className="border border-border rounded-lg p-4 space-y-3 bg-muted/30">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <CheckSquare size={14} />
                  Contrôles qualité
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {formData.quality_checks.map((qc, i) => (
                    <label key={qc.key} className="flex items-center gap-2 cursor-pointer text-sm hover:bg-muted/50 rounded px-2 py-1.5 transition-colors" data-testid={`qc-${qc.key}`}>
                      <input
                        type="checkbox"
                        checked={qc.checked}
                        onChange={() => toggleQualityCheck(i)}
                        className="rounded border-input h-4 w-4 accent-primary"
                      />
                      <span className={qc.checked ? 'text-foreground' : 'text-muted-foreground'}>{qc.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Commentaires */}
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                  <MessageSquare size={14} />
                  Commentaires / Observations
                </label>
                <Textarea
                  data-testid="input-journal-comments"
                  placeholder="Notes sur la fabrication, anomalies constatées..."
                  value={formData.comments}
                  onChange={(e) => setFormData({...formData, comments: e.target.value})}
                  className="min-h-[80px]"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>Annuler</Button>
                <Button data-testid="btn-submit-journal" type="submit">Enregistrer</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {entries.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <p className="text-muted-foreground mb-4">Aucune production enregistrée</p>
            <Button onClick={() => setDialogOpen(true)}>
              <Plus size={18} className="mr-2" /> Première production
            </Button>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {entries.map(entry => (
            <Card key={entry.id}>
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="text-lg font-semibold">{entry.product_name}</h3>
                      <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded">
                        {entry.quantity_lots} lots
                      </span>
                      {entry.batch_number && (
                        <span className="text-xs px-2 py-1 bg-amber-50 text-amber-700 rounded border border-amber-200 font-mono">
                          {entry.batch_number}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Date</p>
                        <p className="font-medium">{new Date(entry.date).toLocaleDateString('fr-FR')}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Coût Total Production</p>
                        <p className="font-medium text-lg">{entry.total_production_cost.toFixed(2)}€</p>
                      </div>
                    </div>

                    {entry.ingredient_lots && entry.ingredient_lots.length > 0 && (
                      <div className="mt-3">
                        <button
                          data-testid={`btn-toggle-lots-${entry.id}`}
                          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                          onClick={() => setExpandedEntry(expandedEntry === entry.id ? null : entry.id)}
                        >
                          {expandedEntry === entry.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          Lots ingrédients ({entry.ingredient_lots.length})
                        </button>
                        {expandedEntry === entry.id && (
                          <div className="mt-2 pl-4 border-l-2 border-border space-y-1">
                            {entry.ingredient_lots.map((il, j) => (
                              <div key={j} className="flex items-center gap-2 text-sm">
                                <span className="text-muted-foreground">{il.material_name}:</span>
                                <span className="font-mono text-xs px-1.5 py-0.5 bg-muted rounded">{il.lot_number}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {entry.quality_checks && entry.quality_checks.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {entry.quality_checks.map((qc, j) => (
                          <span key={j} className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-green-50 text-green-700 rounded border border-green-200">
                            <CheckSquare size={11} /> {qc.label}
                          </span>
                        ))}
                      </div>
                    )}

                    {entry.comments && (
                      <div className="mt-2 px-3 py-2 bg-muted/50 rounded text-sm text-muted-foreground italic">
                        {entry.comments}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <Button size="sm" variant="outline" className="gap-1.5" data-testid={`btn-edit-${entry.id}`}
                      onClick={() => openEditDialog(entry)}>
                      <Pencil size={14} /> Modifier
                    </Button>
                    <Button size="sm" variant="outline" className="gap-1.5" data-testid={`btn-print-${entry.id}`}
                      onClick={() => handlePrintOne(entry.id, entry.product_name)}>
                      <Printer size={14} /> Fiche PDF
                    </Button>
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDelete(entry.id)}>
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog Modification */}
      <Dialog open={editDialog.open} onOpenChange={(open) => !open && setEditDialog({ open: false, entry: null })}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier la production — {editDialog.entry?.product_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Date</label>
                <Input type="date" value={editData.date || ''} onChange={(e) => setEditData({...editData, date: e.target.value})} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Nombre de Lots</label>
                <Input type="number" min="1" value={editData.quantity_lots || ''} onChange={(e) => setEditData({...editData, quantity_lots: e.target.value})} />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">N° de Lot</label>
              <Input value={editData.batch_number || ''} onChange={(e) => setEditData({...editData, batch_number: e.target.value})} placeholder="Ex: LOT-2026-001" />
            </div>
            <div className="border border-border rounded-lg p-4 space-y-3 bg-muted/30">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <CheckSquare size={14} /> Contrôles qualité
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {(editData.quality_checks || []).map((qc, i) => (
                  <label key={qc.key} className="flex items-center gap-2 cursor-pointer text-sm hover:bg-muted/50 rounded px-2 py-1.5 transition-colors">
                    <input type="checkbox" checked={qc.checked} onChange={() => {
                      const updated = [...editData.quality_checks];
                      updated[i] = { ...updated[i], checked: !updated[i].checked };
                      setEditData({...editData, quality_checks: updated});
                    }} className="rounded border-input h-4 w-4 accent-primary" />
                    <span className={qc.checked ? 'text-foreground' : 'text-muted-foreground'}>{qc.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                <MessageSquare size={14} /> Commentaires
              </label>
              <Textarea value={editData.comments || ''} onChange={(e) => setEditData({...editData, comments: e.target.value})} className="min-h-[80px]" placeholder="Notes..." />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="ghost" onClick={() => setEditDialog({ open: false, entry: null })}>Annuler</Button>
              <Button onClick={handleSaveEdit} disabled={saving} className="gap-2" data-testid="btn-save-edit">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Pencil size={16} />}
                Enregistrer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductionJournal;
